"use client";

import { useUser } from "@/context/user-context";
import classNames from "classnames";
import Link from "next/link";
import { useId } from "react";
import Icon from "./icon";

type ArticleCategoryProps = {
  category: { id: string; name: string; slug: string };
  className?: string;
  // "tag" mirrors the look of ArticleTag so they can sit side by side
  variant?: "default" | "tag";
};

const ArticleCategory = (props: ArticleCategoryProps) => {
  const ariaId = useId();

  const { user, toggleTopic, isAuthenticated } = useUser();

  const isSubscribed = user
    ? user?.topics?.find(
        (item) => item.id === props.category.id && item.type === "category",
      )
    : false;

  if (props.variant === "tag") {
    const href = `/amne/${props.category.slug}`;

    if (!isAuthenticated) {
      return (
        <Link
          className={classNames(
            "bg-sand-700 px-3 py-2 rounded-sm font-bold",
            props.className,
          )}
          href={href}
        >
          {props.category.name}
        </Link>
      );
    }

    return (
      <span
        role="group"
        aria-labelledby={ariaId}
        className={classNames(
          "btn p-0 rounded-sm hover:bg-sand-800 bg-sand-700",
          props.className,
        )}
      >
        <button
          aria-label={`Följ ${props.category.name}`}
          aria-pressed={Boolean(isSubscribed)}
          onClick={() =>
            user
              ? toggleTopic({
                  type: "category",
                  id: props.category.id,
                  label: props.category.name,
                })
              : null
          }
          className="w-full h-full bg-sand-800 p-3 rounded-l-sm"
        >
          {user && isSubscribed ? (
            <Icon.CheckCircle
              className="size-[18px] fill-green-500 text-green-500"
              aria-label="följer"
            />
          ) : (
            <Icon.PlusCircle className="size-[18px] fill-black-500 text-black-500" />
          )}
        </button>
        <Link
          id={ariaId}
          href={href}
          className="font-bold px-2 text-base text-nowrap"
        >
          {props.category.name}
        </Link>
      </span>
    );
  }

  if (isAuthenticated) {
    return (
      <span
        role="group"
        aria-labelledby={ariaId}
        className={classNames(
          `btn btn-small`,
          isSubscribed ? "bg-sand-700" : "",
          props.className,
        )}
      >
        <button
          aria-label={isSubscribed ? "avfölj ämne" : "följ ämne"}
          onClick={() =>
            user
              ? toggleTopic({
                  type: "category",
                  id: props.category.id,
                  label: props.category.name,
                })
              : null
          }
        >
          {user && isSubscribed ? (
            <Icon.CheckCircle
              className="size-[18px] fill-green-500"
              aria-label="följer"
            />
          ) : (
            <Icon.PlusCircle className="size-[18px] fill-black-500 text-black-500" />
          )}
        </button>
        <Link
          id={ariaId}
          href={`/amne/${props.category.slug}`}
          className="leading-tight text-sm font-bold"
        >
          {props.category.name}
        </Link>
      </span>
    );
  }

  return (
    <span
      role="group"
      aria-labelledby={ariaId}
      className={classNames(
        `border-2 border-sand-700 px-3 py-2 rounded-sm flex items-center space-x-1`,
        isSubscribed ? "bg-sand-700" : "bg-transparent",
        props.className,
      )}
    >
      <Link
        id={ariaId}
        href={`/amne/${props.category.slug}`}
        className="leading-tight text-sm font-bold"
      >
        {props.category.name}
      </Link>
    </span>
  );
};

export default ArticleCategory;
